import type { InventoryItem } from '@clinic-os/shared/types';
import { DEMO_INVENTORY, DEMO_CLINIC_ID } from '../data/demo-data';

export interface InventoryService {
  getAll: (clinicId?: string) => Promise<InventoryItem[]>;
  getById: (id: string) => Promise<InventoryItem | null>;
  getLowStock: (clinicId?: string) => Promise<InventoryItem[]>;
  create: (data: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<InventoryItem>;
  update: (id: string, data: Partial<InventoryItem>) => Promise<InventoryItem>;
  adjustQuantity: (id: string, delta: number) => Promise<InventoryItem>;
}

class InventoryServiceImpl implements InventoryService {
  private items: InventoryItem[] = [...DEMO_INVENTORY];

  async getAll(clinicId = DEMO_CLINIC_ID): Promise<InventoryItem[]> {
    return this.items.filter((i) => i.clinicId === clinicId);
  }

  async getById(id: string): Promise<InventoryItem | null> {
    return this.items.find((i) => i.id === id) ?? null;
  }

  async getLowStock(clinicId = DEMO_CLINIC_ID): Promise<InventoryItem[]> {
    return this.items.filter(
      (i) => i.clinicId === clinicId && i.quantity <= i.lowStockThreshold
    );
  }

  async create(data: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<InventoryItem> {
    const now = new Date().toISOString();
    const item: InventoryItem = {
      ...data,
      id: `inv${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    this.items.push(item);
    return item;
  }

  async update(id: string, data: Partial<InventoryItem>): Promise<InventoryItem> {
    const idx = this.items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error(`Inventory item ${id} not found`);
    const updated: InventoryItem = { ...this.items[idx]!, ...data, updatedAt: new Date().toISOString() };
    this.items[idx] = updated;
    return updated;
  }

  async adjustQuantity(id: string, delta: number): Promise<InventoryItem> {
    const item = await this.getById(id);
    if (!item) throw new Error(`Item ${id} not found`);
    return this.update(id, { quantity: item.quantity + delta });
  }
}

export const inventoryService = new InventoryServiceImpl();
